import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { AppDispatch, RootState } from '../store/store';
import {
  addAddressDetailsAsync,
  fetchLandmarkThunk,
} from '../store/purchaseSlice';

interface SessionData {
  sessionId: string;
  storeId: string;
  phoneNumber: string;
  // True when the store uses LocationGroups delivery mode; the picker then collects the
  // delivery area here so the WhatsApp delivery-area step can be skipped entirely.
  usesDeliveryLocationGroups?: boolean;
}

interface SelectedAddress {
  address: string;
  latitude: number | string;
  longitude: number | string;
  deliveryPhoneNumber: string;
  deliveryInstructions: string;
  state: string;
}

const extractState = (
  components?: google.maps.GeocoderAddressComponent[]
): string =>
  components?.find((c) => c.types.includes('administrative_area_level_1'))
    ?.long_name ?? '';

const AddressPicker: React.FC = () => {
  const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  const dispatch = useDispatch<AppDispatch>();
  const { storeId: storeIdParam } = useParams<{ storeId: string }>();
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { storeId } = useSelector((state: RootState) => state.setup);
  const landmarks = useSelector((state: RootState) => state.purchase.landmarks);
  const [deliveryLocationId, setDeliveryLocationId] = useState<number | ''>('');
  const usesDeliveryLocationGroups =
    sessionData?.usesDeliveryLocationGroups ?? false;
  const [loadingMessage, setLoadingMessage] = useState(
    'Start typing to search for addresses...'
  );
  const [predictions, setPredictions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [selectedAddress, setSelectedAddress] =
    useState<SelectedAddress | null>({
      address: '',
      latitude: '',
      longitude: '',
      deliveryPhoneNumber: '',
      deliveryInstructions: '',
      state: '',
    });
  const [isLoading, setIsLoading] = useState(false);

  const autocompleteService =
    useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const geocoder = useRef<google.maps.Geocoder | null>(null);

  // --- Load Google Maps script dynamically ---
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const encodedData = urlParams.get('data');
    if (encodedData) {
      try {
        const decoded = JSON.parse(atob(encodedData));
        setSessionData(decoded);
      } catch (err) {
        setLoadingMessage(
          'Invalid URL parameters. Please use the link from WhatsApp.'
        );
      }
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&callback=initGoogleMaps`;
    script.async = true;
    script.defer = true;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).initGoogleMaps = initGoogleMaps;

    document.head.appendChild(script);

    return () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (window as any).initGoogleMaps;
    };
  }, [GOOGLE_MAPS_API_KEY]);

  // --- Fetch delivery areas when the store uses location groups ---
  // The delivery-locations endpoint is gated by the x-store-id header (see AuthService), which
  // is read from localStorage. The picker route does not set it, so seed it from the URL first.
  useEffect(() => {
    if (storeIdParam) {
      localStorage.setItem('storeId', storeIdParam);
    }
  }, [storeIdParam]);

  useEffect(() => {
    if (usesDeliveryLocationGroups && storeIdParam) {
      dispatch(fetchLandmarkThunk());
    }
  }, [usesDeliveryLocationGroups, storeIdParam, dispatch]);

  // --- Initialize Google Places/Geocode ---
  const initGoogleMaps = () => {
    autocompleteService.current = new google.maps.places.AutocompleteService();
    const mapDiv = document.createElement('div');
    const map = new google.maps.Map(mapDiv);
    placesService.current = new google.maps.places.PlacesService(map);
    geocoder.current = new google.maps.Geocoder();
  };

  // --- Search addresses ---
  useEffect(() => {
    if (!autocompleteService.current || searchQuery.length < 3) {
      setPredictions([]);
      setLoadingMessage('Start typing to search for addresses...');
      return;
    }

    const handler = setTimeout(() => {
      setLoadingMessage('Searching...');
      autocompleteService.current?.getPlacePredictions(
        {
          input: searchQuery,
          types: ['address'],
          componentRestrictions: { country: 'ng' },
        },
        (results, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && results) {
            setPredictions(results);
          } else {
            setPredictions([]);
            setLoadingMessage(
              'No addresses found. Try a different search term.'
            );
          }
        }
      );
    }, 300);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  // --- Select address ---
  const handleSelect = (
    prediction: google.maps.places.AutocompletePrediction
  ) => {
    if (!placesService.current) return;
    setSearchQuery(prediction.description);

    placesService.current.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['formatted_address', 'geometry', 'address_components'],
      },
      (place, status) => {
        if (
          status === google.maps.places.PlacesServiceStatus.OK &&
          place?.geometry
        ) {
          setSelectedAddress({
            address: place.formatted_address ?? '',
            latitude: place.geometry.location?.lat() ?? 0,
            longitude: place.geometry.location?.lng() ?? 0,
            deliveryPhoneNumber: selectedAddress?.deliveryPhoneNumber
              ? selectedAddress.deliveryPhoneNumber
              : '',
            deliveryInstructions: selectedAddress?.deliveryInstructions
              ? selectedAddress.deliveryInstructions
              : '',
            state: extractState(place.address_components),
          });
        }
      }
    );
  };

  // --- Use current location ---
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLoadingMessage('Geolocation is not supported by this browser.');
      return;
    }

    setLoadingMessage('Getting your current location...');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const latlng = new google.maps.LatLng(latitude, longitude);

        geocoder.current?.geocode({ location: latlng }, (results, status) => {
          if (
            status === google.maps.GeocoderStatus.OK &&
            results &&
            results[0] &&
            selectedAddress &&
            selectedAddress.deliveryInstructions &&
            selectedAddress.deliveryPhoneNumber
          ) {
            setSelectedAddress({
              address: results[0].formatted_address,
              latitude,
              longitude,
              deliveryPhoneNumber:
                selectedAddress.deliveryPhoneNumber.toString(),
              deliveryInstructions:
                selectedAddress.deliveryInstructions.toString(),
              state: extractState(results[0].address_components),
            });
            setPredictions([]);
          } else {
            setLoadingMessage(
              'Unable to get address for your current location.'
            );
          }
        });
      },
      (err) => setLoadingMessage(`Location error: ${err.message}`)
    );
  };

  // --- Continue to WhatsApp ---
  const continueToWhatsApp = async () => {
    if (!selectedAddress || !sessionData) return;
    setIsLoading(true);

    try {
      if (storeId) {
        dispatch(
          addAddressDetailsAsync({
            sessionId: sessionData.sessionId,
            body: {
              longitude: selectedAddress.longitude.toString(),
              latitude: selectedAddress.latitude.toString(),
              address: selectedAddress.address,
              deliveryPhoneNumber: selectedAddress.deliveryPhoneNumber,
              deliveryInstructions: selectedAddress.deliveryInstructions,
              state: selectedAddress.state,
              deliveryLocationId:
                deliveryLocationId === '' ? undefined : deliveryLocationId,
            },
          })
        );
      }

      setTimeout(() => {
        // Normalize phone number and open WhatsApp chat\
        const whatsAppNumber =
          localStorage.getItem('mimicWhatsAppPhoneNumber') + '';
        const normalizedNumber = whatsAppNumber.replace(/\D/g, '');

        window.open(`https://wa.me/${normalizedNumber}`, '_blank');
        setIsLoading(false);
      }, 300);
    } catch (err) {
      setIsLoading(false);
      setLoadingMessage('Failed to save address. Please try again.');
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center bg-gray-100 p-4">
      <div className="max-w-md w-full bg-white shadow-md rounded-lg flex flex-col min-h-[80vh] overflow-hidden">
        <header className="flex justify-between items-center p-4 border-b">
          <div className="text-[16px] font-regular">Delivery Details</div>
          <span className="text-[8px] text-gray-400">Powered by IziFin</span>
        </header>
        <div>
          <div className="px-[15px] py-[10px]">
            <div className="text-[12px] mb-3">
              Delivery Phone Number (Optional)
            </div>
            <input
              type="text"
              name="phoneNumber"
              className="w-full bg-gray-50 rounded-md text-[12px]
            h-[45px] border-[1px] border-gray-100 p-[10px]"
              placeholder="Enter Phone Number"
              onChange={(e) => {
                if (selectedAddress) {
                  setSelectedAddress({
                    address: selectedAddress.address,
                    latitude: selectedAddress.latitude,
                    longitude: selectedAddress.longitude,
                    deliveryPhoneNumber: e.target.value,
                    deliveryInstructions:
                      selectedAddress.deliveryInstructions.toString(),
                    state: selectedAddress.state,
                  });
                }
              }}
            />
            <div className="text-[12px] mt-3">
              Delivery Instructions(Optional)
            </div>

            <textarea
              name="instruction"
              placeholder="Delivery Instructions"
              className="w-full bg-gray-50 rounded-md text-[12px]
            h-[70px] mt-2 border-[1px] border-gray-100 p-[10px]"
              onChange={(e) => {
                if (selectedAddress) {
                  setSelectedAddress({
                    address: selectedAddress.address,
                    latitude: selectedAddress.latitude,
                    longitude: selectedAddress.longitude,
                    deliveryPhoneNumber: selectedAddress.deliveryPhoneNumber,
                    deliveryInstructions: e.target.value,
                    state: selectedAddress.state,
                  });
                }
              }}
            />

            {usesDeliveryLocationGroups && (
              <>
                <div className="text-[12px] mt-3">Delivery Area</div>
                <select
                  name="deliveryArea"
                  value={
                    deliveryLocationId === '' ? '' : String(deliveryLocationId)
                  }
                  onChange={(e) =>
                    setDeliveryLocationId(
                      e.target.value === '' ? '' : Number(e.target.value)
                    )
                  }
                  className="w-full bg-gray-50 rounded-md text-[12px]
            h-[45px] mt-2 border-[1px] border-gray-100 p-[10px]"
                >
                  <option value="">-- Select your delivery area --</option>
                  {landmarks.map((landmark) => (
                    <option key={landmark.id} value={landmark.id}>
                      {landmark.area}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>
        </div>
        <div className="px-4 pb-4 border-b">
          <h1 className="text-[12px] mb-[15px] font-regular">
            Enter Delivery Address
          </h1>
          <div className="relative">
            <input
              type="text"
              placeholder="Search for an address..."
              className="w-full border rounded-md p-3 pl-10 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-green-500 outline-none"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <span className="absolute left-3 top-3.5 text-gray-400">🔍</span>
          </div>
        </div>

        <button
          onClick={getCurrentLocation}
          className="flex items-center gap-2 bg-white text-green-600 p-4 border-b hover:bg-gray-50"
        >
          📍 <span>Use your current location</span>
        </button>

        <div className="flex-1 overflow-y-auto">
          {predictions.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              {loadingMessage}
            </div>
          ) : (
            predictions.map((pred) => (
              <div
                key={pred.place_id}
                className={`p-4 border-b cursor-pointer hover:bg-gray-50 ${
                  selectedAddress?.address === pred.description
                    ? 'bg-blue-50 border-blue-400'
                    : ''
                }`}
                onClick={() => {
                  handleSelect(pred);
                }}
              >
                <div className="font-medium text-gray-800">
                  {pred.structured_formatting.main_text}
                </div>
                <div className="text-sm text-gray-500">
                  {pred.structured_formatting.secondary_text}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      {selectedAddress?.address}
      {selectedAddress?.address !== '' && (
        <button
          disabled={
            isLoading ||
            (usesDeliveryLocationGroups && deliveryLocationId === '')
          }
          onClick={continueToWhatsApp}
          className={`fixed bottom-5 px-6 py-3 rounded-full font-semibold text-white shadow-md transition ${
            isLoading ||
            (usesDeliveryLocationGroups && deliveryLocationId === '')
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-green-500 hover:bg-green-600'
          }`}
        >
          {isLoading
            ? 'Saving address...'
            : usesDeliveryLocationGroups && deliveryLocationId === ''
              ? 'Select a delivery area'
              : 'Continue on WhatsApp'}
        </button>
      )}
    </div>
  );
};

export default AddressPicker;
